import React, { useState } from 'react';
import { ShieldAlert, AlertCircle, User, BookOpen, Sparkles, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useSubscription } from '@/hooks/useSubscription';
import { usePersonas } from '@/hooks/usePersonas';
import { useContexts } from '@/hooks/useContexts';
import { supabase } from '@/lib/supabase';
import { PricingModal } from './PricingModal';
import { analytics } from '@/services/analyticsService';

export const PlanComplianceModal: React.FC = () => {
    const { 
        hasResourceOverflow, 
        excessPersonasCount, 
        excessContextsCount, 
        personasCount, 
        contextsCount 
    } = useSubscription();

    const { personas } = usePersonas();
    const { contexts } = useContexts();

    const [selectedPersonaIds, setSelectedPersonaIds] = useState<string[]>([]);
    const [selectedContextIds, setSelectedContextIds] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);

    React.useEffect(() => {
        if (hasResourceOverflow) {
            analytics.track('plan_compliance_viewed', {
                excess_personas: excessPersonasCount,
                excess_contexts: excessContextsCount,
            });
        }
    }, [hasResourceOverflow, excessPersonasCount, excessContextsCount]);

    if (!hasResourceOverflow) return null;

    const remainingPersonasSelected = Math.max(0, excessPersonasCount - selectedPersonaIds.length);
    const remainingContextsSelected = Math.max(0, excessContextsCount - selectedContextIds.length);
    const isSelectionComplete = remainingPersonasSelected === 0 && remainingContextsSelected === 0;

    const togglePersona = (id: string) => {
        setSelectedPersonaIds(prev =>
            prev.includes(id) ? prev.filter(pId => pId !== id) : [...prev, id]
        );
    };

    const toggleContext = (id: string) => {
        setSelectedContextIds(prev =>
            prev.includes(id) ? prev.filter(cId => cId !== id) : [...prev, id]
        );
    };

    const handleResolveCompliance = async () => {
        if (!isSelectionComplete) return;
        setIsSubmitting(true);
        setErrorMessage('');

        try {
            // 1. Limpar arquivos do Storage para os contextos selecionados
            if (selectedContextIds.length > 0) {
                const contextsToDelete = contexts.filter(c => selectedContextIds.includes(c.id));
                for (const ctx of contextsToDelete) {
                    const files = ctx.files || [];
                    const paths = files.map(f => f.path).filter((p): p is string => Boolean(p));
                    if (paths.length > 0) {
                        try {
                            await supabase.storage.from('context_files').remove(paths);
                        } catch (err) {
                            console.error('[PlanComplianceModal] Erro ao limpar arquivos do Storage:', err);
                        }
                    }
                }
            }

            // 2. Chamar RPC atômico no PostgreSQL
            const { error } = await supabase.rpc('resolve_basic_plan_compliance', {
                p_delete_persona_ids: selectedPersonaIds,
                p_delete_context_ids: selectedContextIds,
            });

            if (error) throw error;

            // 3. Registrar telemetria da adequação de plano
            analytics.track('plan_compliance_resolved', {
                deleted_personas_count: selectedPersonaIds.length,
                deleted_contexts_count: selectedContextIds.length,
            });

            // 4. Recarregar a página para atualizar todos os caches
            window.location.reload();
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Erro ao processar adequação de plano.';
            console.error('Erro ao resolver conformidade do plano:', err);
            setErrorMessage(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-300">
                <div 
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="plan-compliance-title"
                    aria-describedby="plan-compliance-desc"
                    className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden border border-slate-200"
                >
                    
                    {/* Header */}
                    <div className="p-6 md:p-8 bg-gradient-to-br from-amber-50 to-orange-50/40 border-b border-amber-100/80">
                        <div className="flex items-center gap-4">
                            <div className="p-3.5 bg-amber-100/90 text-amber-700 rounded-2xl shrink-0 shadow-xs border border-amber-200/50">
                                <ShieldAlert size={28} />
                            </div>
                            <div>
                                <h2 id="plan-compliance-title" className="text-xl font-bold text-slate-900">
                                    Adequação ao Plano Basic
                                </h2>
                                <p id="plan-compliance-desc" className="text-sm text-slate-600 mt-1 leading-relaxed">
                                    O Plano Basic permite até <strong className="text-slate-900">3 personas</strong> e até <strong className="text-slate-900">3 contextos personalizados</strong>. Selecione os itens que deseja remover para continuar, ou faça upgrade para o Pro para manter todos os seus dados.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Scrollable Content */}
                    <div className="p-6 md:p-8 overflow-y-auto space-y-6 flex-1">
                        
                        {/* Seção de Personas Excedentes */}
                        {excessPersonasCount > 0 && (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <User size={18} className="text-indigo-600" />
                                        <h3 className="text-sm font-bold text-slate-900">
                                            Personas Cadastradas ({personasCount}/3)
                                        </h3>
                                    </div>
                                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                                        remainingPersonasSelected === 0 
                                            ? 'bg-emerald-100 text-emerald-800' 
                                            : 'bg-amber-100 text-amber-900 animate-pulse'
                                    }`}>
                                        {remainingPersonasSelected === 0 
                                            ? '✓ Personas selecionadas' 
                                            : `Selecione mais ${remainingPersonasSelected} para excluir`}
                                    </span>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                                    {personas.map(persona => {
                                        const isSelected = selectedPersonaIds.includes(persona.id);
                                        return (
                                            <div
                                                key={persona.id}
                                                onClick={() => togglePersona(persona.id)}
                                                className={`p-3 rounded-xl border cursor-pointer flex items-center gap-3 transition-all ${
                                                    isSelected 
                                                        ? 'bg-rose-50/80 border-rose-400 ring-2 ring-rose-400/30' 
                                                        : 'bg-white border-slate-200 hover:border-slate-300'
                                                }`}
                                            >
                                                <div className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 transition-colors ${
                                                    isSelected ? 'bg-rose-600 border-rose-600 text-white' : 'border-slate-300 bg-white'
                                                }`}>
                                                    {isSelected && <Trash2 size={12} />}
                                                </div>
                                                <div className="w-8 h-8 rounded-full overflow-hidden bg-slate-100 shrink-0 border border-slate-200 flex items-center justify-center">
                                                    {persona.avatarUrl ? (
                                                        <img src={persona.avatarUrl} alt={persona.name} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <User size={16} className="text-slate-400" />
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-bold text-slate-900 truncate">{persona.name}</p>
                                                    <p className="text-xs text-slate-500 truncate">{persona.occupation || 'Persona'}</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Seção de Contextos Excedentes */}
                        {excessContextsCount > 0 && (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <BookOpen size={18} className="text-violet-600" />
                                        <h3 className="text-sm font-bold text-slate-900">
                                            Contextos Criados ({contextsCount}/3)
                                        </h3>
                                    </div>
                                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                                        remainingContextsSelected === 0 
                                            ? 'bg-emerald-100 text-emerald-800' 
                                            : 'bg-amber-100 text-amber-900 animate-pulse'
                                    }`}>
                                        {remainingContextsSelected === 0 
                                            ? '✓ Contextos selecionados' 
                                            : `Selecione mais ${remainingContextsSelected} para excluir`}
                                    </span>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                                    {contexts.map(context => {
                                        const isSelected = selectedContextIds.includes(context.id);
                                        return (
                                            <div
                                                key={context.id}
                                                onClick={() => toggleContext(context.id)}
                                                className={`p-3 rounded-xl border cursor-pointer flex items-center gap-3 transition-all ${
                                                    isSelected 
                                                        ? 'bg-rose-50/80 border-rose-400 ring-2 ring-rose-400/30' 
                                                        : 'bg-white border-slate-200 hover:border-slate-300'
                                                }`}
                                            >
                                                <div className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 transition-colors ${
                                                    isSelected ? 'bg-rose-600 border-rose-600 text-white' : 'border-slate-300 bg-white'
                                                }`}>
                                                    {isSelected && <Trash2 size={12} />}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-bold text-slate-900 truncate">{context.name}</p>
                                                    <p className="text-xs text-slate-500 truncate">{context.description || 'Contexto personalizado'}</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Aviso de Transparência & Impacto da Exclusão */}
                        <div className="p-4 bg-rose-50/60 border border-rose-200 rounded-2xl flex items-start gap-3">
                            <AlertCircle size={18} className="text-rose-600 shrink-0 mt-0.5" />
                            <div className="text-xs text-rose-900 leading-relaxed">
                                <strong>Aviso de exclusão permanente:</strong> Ao confirmar, os itens selecionados e <u>todas as conversas, mensagens e insights associados a eles</u> serão excluídos definitivamente para adequação à cota.
                            </div>
                        </div>

                        {errorMessage && (
                            <div className="p-3 bg-red-100 border border-red-200 text-red-700 text-xs font-semibold rounded-xl">
                                {errorMessage}
                            </div>
                        )}
                    </div>

                    {/* Footer Actions */}
                    <div className="p-6 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3">
                        <Button
                            variant="secondary"
                            onClick={() => setIsPricingModalOpen(true)}
                            icon={Sparkles}
                            className="w-full sm:w-auto text-indigo-700 border-indigo-200 bg-indigo-50/50 hover:bg-indigo-100/80"
                        >
                            Fazer Upgrade para o Pro (Manter Tudo)
                        </Button>

                        <Button
                            variant="danger"
                            onClick={handleResolveCompliance}
                            disabled={!isSelectionComplete || isSubmitting}
                            loading={isSubmitting}
                            icon={Trash2}
                            className="w-full sm:w-auto"
                        >
                            {isSubmitting ? 'Excluindo...' : 'Excluir Selecionados e Continuar'}
                        </Button>
                    </div>
                </div>
            </div>

            <PricingModal
                isOpen={isPricingModalOpen}
                onClose={() => setIsPricingModalOpen(false)}
                title="Mantenha Todas as Suas Personas e Contextos"
                subtitle="Com o Plano Pro, você tem acesso ilimitado a personas, contextos e extração de insights por IA."
            />
        </>
    );
};
